/**
 * Session management API schemas
 */

import { z, ZodType } from 'zod';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { Role } from '../identity-contracts';
import {
    AuthenticatedRawSession,
    DatabaseSessionData,
    DatabaseSessionCookie,
    ExpressSessionJson,
    ExpressRuntimeSessionJson,
    ExpressSessionJsonStorageV1,
    GuestTokenRawSession,
    Impersonator,
    ParsedSessionState,
    PendingMfaRawSession,
    RawSessionBase,
    RuntimeSessionCookie,
    SessionsSearchRequest,
    SessionListItem,
    UnauthenticatedRawSession,
} from '../session.types';
import { SearchTermsSchema } from './omnisearch.schemas';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    defineStoredJsonSemanticFieldValue,
} from '../stored-json-contract.types';
import {
    StoredJsonNumberRecordSchema,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';

export const GuestTokenSessionSnapshotSchema = z.object({
    single_use_auth_token_id: z.number().int().positive(),
    token_type: z.literal('PAYMENT_REQUEST_ACCESS'),
    payment_request_id: z.number().int().positive(),
    payment_request_uuid: z.string().uuid(),
    expires_at: z.string().datetime(),
});

export const DatabaseSessionCookieSchema: ZodType<DatabaseSessionCookie> = z
    .object({
        secure: z.boolean().optional(),
        httpOnly: z.boolean().optional(),
        domain: z.string().optional(),
        path: z.string().optional(),
        sameSite: z.union([z.boolean(), z.enum(['strict', 'lax', 'none'])]).optional(),
        maxAge: z.number().int().nonnegative().nullable().optional(),
        originalMaxAge: z.number().int().nonnegative().nullable().optional(),
    })
    .catchall(z.unknown());

export const RuntimeSessionCookieSchema: ZodType<RuntimeSessionCookie> = z
    .object({
        secure: z.union([z.boolean(), z.literal('auto')]).optional(),
        httpOnly: z.boolean().optional(),
        domain: z.string().optional(),
        path: z.string().optional(),
        sameSite: z.union([z.boolean(), z.enum(['strict', 'lax', 'none'])]).optional(),
        maxAge: z.number().int().nullable().optional(),
        originalMaxAge: z.number().int().nonnegative().nullable().optional(),
    })
    .catchall(z.unknown());

/**
 * Database session data structure
 * Note: CookieOptions from express-session is complex, so we use z.unknown() for cookie
 */
export const DatabaseSessionDataSchema: ZodType<DatabaseSessionData> = z.object({
    passport: z.object({
        user: z.number(),
    }),
    user_id: z.number().optional(),
    ip_address: z.string(),
    user_agent: z.string(),
    cookie: DatabaseSessionCookieSchema, // CookieOptions from express-session, not representable as JSON
    active_role_assignment_id: z.number().nullable(),
    session_uuid: z.string().uuid().optional(),
    active_role_assignment_uuid: z.string().uuid().nullable().optional(),
    impersonator: z
        .object({
            user_id: z.number(),
            role_assignment_id: z.number(),
        })
        .nullable(),
    enable_javascript_console: z.boolean(),
});

export const GuestTokenSessionDataSchema = z.object({
    guest_token: GuestTokenSessionSnapshotSchema,
    ip_address: z.string(),
    user_agent: z.string(),
    cookie: DatabaseSessionCookieSchema,
    enable_javascript_console: z.boolean(),
});

export const RawSessionObjectSchema: z.ZodType<Record<string, unknown>> = z
    .object({})
    .catchall(z.unknown());

export const RawSessionPassportSchema: z.ZodType<AuthenticatedRawSession['passport']> = z
    .object({
        user: z.number().int(),
    })
    .catchall(z.unknown());

export const RawSessionImpersonatorSchema: z.ZodType<Impersonator> = z.object({
    user_id: z.number().int(),
    role_assignment_id: z.number().int(),
});

export const RawSessionBaseSchema: z.ZodType<RawSessionBase> = z
    .object({
        id: z.string().min(1).optional(),
        session_uuid: z.string().uuid().optional(),
        audit_id: z.number().int().nullable().optional(),
        ip_address: z.string().min(1).nullable().optional(),
        user_agent: z.string().min(1).nullable().optional(),
        cookie: DatabaseSessionCookieSchema.nullable().optional(),
    })
    .catchall(z.unknown());

export const AuthenticatedRawSessionSchema: z.ZodType<AuthenticatedRawSession> = z
    .object({
        id: z.string().min(1).optional(),
        session_uuid: z.string().uuid().optional(),
        audit_id: z.number().int().nullable().optional(),
        passport: RawSessionPassportSchema,
        ip_address: z.string().min(1).nullable().optional(),
        user_agent: z.string().min(1).nullable().optional(),
        cookie: DatabaseSessionCookieSchema,
        active_role_assignment_id: z.number().int().nullable(),
        active_role_assignment_uuid: z.string().uuid().nullable().optional(),
        impersonator: RawSessionImpersonatorSchema.nullable(),
        enable_javascript_console: z.boolean().optional(),
        requires_password_change: z.boolean().optional(),
        guest_token: GuestTokenSessionSnapshotSchema.nullable().optional(),
        pending_mfa_user_id: z.number().int().positive().nullable().optional(),
        pending_mfa_created_at_ms: z.number().int().nonnegative().nullable().optional(),
        pending_mfa_login_email: z.string().nullable().optional(),
        pending_mfa_payment_request_uuid: z.string().nullable().optional(),
        pending_mfa_requires_password_change: z.boolean().nullable().optional(),
        pending_mfa_password_authenticated: z.boolean().nullable().optional(),
        pending_mfa_setup_required: z.boolean().nullable().optional(),
        pending_totp_user_id: z.number().int().positive().nullable().optional(),
        pending_totp_created_at_ms: z.number().int().nonnegative().nullable().optional(),
        pending_totp_login_email: z.string().nullable().optional(),
        pending_totp_payment_request_uuid: z.string().nullable().optional(),
        pending_totp_requires_password_change: z.boolean().nullable().optional(),
    })
    .catchall(z.unknown());

export const GuestTokenRawSessionSchema: z.ZodType<GuestTokenRawSession> = z
    .object({
        id: z.string().min(1).optional(),
        session_uuid: z.string().uuid().optional(),
        audit_id: z.number().int().nullable().optional(),
        ip_address: z.string().min(1).nullable().optional(),
        user_agent: z.string().min(1).nullable().optional(),
        cookie: DatabaseSessionCookieSchema.nullable().optional(),
        guest_token: GuestTokenSessionSnapshotSchema,
    })
    .catchall(z.unknown());

export const PendingMfaRawSessionSchema: z.ZodType<PendingMfaRawSession> = z
    .object({
        id: z.string().min(1).optional(),
        session_uuid: z.string().uuid().optional(),
        audit_id: z.number().int().nullable().optional(),
        ip_address: z.string().min(1).nullable().optional(),
        user_agent: z.string().min(1).nullable().optional(),
        cookie: DatabaseSessionCookieSchema.nullable().optional(),
        guest_token: GuestTokenSessionSnapshotSchema.nullable().optional(),
        pending_mfa_user_id: z.number().int().positive().nullable().optional(),
        pending_mfa_created_at_ms: z.number().int().nonnegative().nullable().optional(),
        pending_mfa_login_email: z.string().nullable().optional(),
        pending_mfa_payment_request_uuid: z.string().nullable().optional(),
        pending_mfa_requires_password_change: z.boolean().nullable().optional(),
        pending_mfa_password_authenticated: z.boolean().nullable().optional(),
        pending_mfa_setup_required: z.boolean().nullable().optional(),
        pending_totp_user_id: z.number().int().positive().nullable().optional(),
        pending_totp_created_at_ms: z.number().int().nonnegative().nullable().optional(),
        pending_totp_login_email: z.string().nullable().optional(),
        pending_totp_payment_request_uuid: z.string().nullable().optional(),
        pending_totp_requires_password_change: z.boolean().nullable().optional(),
    })
    .catchall(z.unknown());

export const UnauthenticatedRawSessionSchema: z.ZodType<UnauthenticatedRawSession> = z
    .object({
        id: z.string().min(1).optional(),
        session_uuid: z.string().uuid().optional(),
        audit_id: z.number().int().nullable().optional(),
        ip_address: z.string().min(1).nullable().optional(),
        user_agent: z.string().min(1).nullable().optional(),
        cookie: DatabaseSessionCookieSchema.nullable().optional(),
        active_role_assignment_id: z.number().int().nullable().optional(),
        user_id: z.union([z.number(), z.string()]).optional(),
    })
    .catchall(z.unknown());

export const ParsedNoSessionStateSchema: ReturnType<
    typeof z.strictObject<{
        kind: z.ZodLiteral<'no_session'>;
    }>
> = z.strictObject({
    kind: z.literal('no_session'),
});

export const ParsedUnauthenticatedSessionStateSchema: ReturnType<
    typeof z.strictObject<{
        kind: z.ZodLiteral<'unauthenticated'>;
        session: typeof UnauthenticatedRawSessionSchema;
    }>
> = z.strictObject({
    kind: z.literal('unauthenticated'),
    session: UnauthenticatedRawSessionSchema,
});

export const ParsedGuestTokenSessionStateSchema: ReturnType<
    typeof z.strictObject<{
        kind: z.ZodLiteral<'guest_token'>;
        session: typeof GuestTokenRawSessionSchema;
    }>
> = z.strictObject({
    kind: z.literal('guest_token'),
    session: GuestTokenRawSessionSchema,
});

export const ParsedPendingMfaSessionStateSchema: ReturnType<
    typeof z.strictObject<{
        kind: z.ZodLiteral<'pending_mfa'>;
        session: typeof PendingMfaRawSessionSchema;
    }>
> = z.strictObject({
    kind: z.literal('pending_mfa'),
    session: PendingMfaRawSessionSchema,
});

export const ParsedAuthenticatedSessionStateSchema: ReturnType<
    typeof z.strictObject<{
        kind: z.ZodLiteral<'authenticated'>;
        session: typeof AuthenticatedRawSessionSchema;
        databaseSessionData: typeof DatabaseSessionDataSchema;
    }>
> = z.strictObject({
    kind: z.literal('authenticated'),
    session: AuthenticatedRawSessionSchema,
    databaseSessionData: DatabaseSessionDataSchema,
});

export const ParsedSessionStateSchema: z.ZodType<ParsedSessionState> = z.discriminatedUnion(
    'kind',
    [
        ParsedNoSessionStateSchema,
        ParsedUnauthenticatedSessionStateSchema,
        ParsedGuestTokenSessionStateSchema,
        ParsedPendingMfaSessionStateSchema,
        ParsedAuthenticatedSessionStateSchema,
    ]
);

export const ExpressSessionJsonStorageV1Schema: z.ZodType<ExpressSessionJsonStorageV1> = z
    .unknown()
    .transform((rawSession: unknown, ctx): ExpressSessionJsonStorageV1 => {
        try {
            const parsedSessionState: ParsedSessionState = parseStoredSessionState(rawSession);
            if (parsedSessionState.kind === 'no_session') {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Stored Express session JSON must be an object',
                });
                return z.NEVER;
            }
            return parsedSessionState.session;
        } catch (error: unknown) {
            ctx.addIssue({
                code: 'custom',
                message: error instanceof Error ? error.message : 'Invalid session JSON',
            });
            return z.NEVER;
        }
    });

function normalizeStoredExpressSessionJson(value: unknown): unknown {
    const normalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(value, {
        domain: 'express-session-json',
        fields: [
            'audit_id',
            'active_role_assignment_id',
            'user_id',
            'pending_mfa_user_id',
            'pending_mfa_created_at_ms',
            'pending_totp_user_id',
            'pending_totp_created_at_ms',
        ],
        minimum: Number.MIN_SAFE_INTEGER,
        maximum: Number.MAX_SAFE_INTEGER,
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedRoot);
    if (!rootResult.success) {
        return normalizedRoot;
    }

    const normalized: Record<string, unknown> = { ...rootResult.data };
    delete normalized.pending_mfa_active_role_assignment_id;
    delete normalized.pending_totp_active_role_assignment_id;
    const nestedFields: ReadonlyArray<{
        readonly field: string;
        readonly numericFields: readonly string[];
    }> = [
        { field: 'passport', numericFields: ['user'] },
        {
            field: 'impersonator',
            numericFields: ['user_id', 'role_assignment_id'],
        },
        {
            field: 'guest_token',
            numericFields: ['single_use_auth_token_id', 'payment_request_id'],
        },
        {
            field: 'cookie',
            numericFields: ['maxAge', 'originalMaxAge'],
        },
    ];
    for (const nestedField of nestedFields) {
        if (!Object.hasOwn(rootResult.data, nestedField.field)) {
            continue;
        }
        normalized[nestedField.field] = normalizeStoredJsonSafeIntegerObjectFields(
            rootResult.data[nestedField.field],
            {
                domain: `express-session-json.${nestedField.field}`,
                fields: nestedField.numericFields,
                minimum: Number.MIN_SAFE_INTEGER,
                maximum: Number.MAX_SAFE_INTEGER,
            }
        );
    }
    return normalized;
}

export const StoredAuthenticatedRawSessionSchema: z.ZodType<AuthenticatedRawSession> = z.preprocess(
    normalizeStoredExpressSessionJson,
    AuthenticatedRawSessionSchema
);

export const StoredExpressSessionJsonStorageV1Schema: z.ZodType<ExpressSessionJsonStorageV1> =
    z.preprocess(normalizeStoredExpressSessionJson, ExpressSessionJsonStorageV1Schema);

export function normalizeExpressSessionJsonStorageV1(
    session: ExpressSessionJsonStorageV1
): ParsedSessionState {
    return parseStoredSessionState(session);
}

export const ExpressSessionJsonStorageContract = defineStoredJsonContract({
    contractName: 'ExpressSessionJson',
    semanticFieldValues: {
        sessionUuid: defineStoredJsonSemanticFieldValue<string>('scalar'),
        userId: defineStoredJsonSemanticFieldValue<number>('scalar'),
        activeRoleAssignmentId: defineStoredJsonSemanticFieldValue<number | null>('scalar'),
        ipAddress: defineStoredJsonSemanticFieldValue<string | null>('scalar'),
        userAgent: defineStoredJsonSemanticFieldValue<string | null>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'StoredExpressSessionJsonStorageV1Schema',
            schema: StoredExpressSessionJsonStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['express_session_json_version'],
                },
            ],
            normalize: normalizeExpressSessionJsonStorageV1,
            semanticPaths: {
                sessionUuid: ['session_uuid'],
                userId: ['passport', 'user'],
                activeRoleAssignmentId: ['active_role_assignment_id'],
                ipAddress: ['ip_address'],
                userAgent: ['user_agent'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredExpressSessionJsonRowParser: z.ZodType<ParsedSessionState> =
    buildStoredJsonParser(ExpressSessionJsonStorageContract);

export const StoredExpressSessionJsonParser: z.ZodType<ParsedSessionState> = z.preprocess(
    (value: unknown): unknown => ({ value }),
    StoredExpressSessionJsonRowParser
);

export function serializeExpressSessionJsonV1(
    session: ExpressSessionJson | ExpressRuntimeSessionJson
): ExpressSessionJsonStorageV1 {
    const rawSessionObject: Record<string, unknown> = RawSessionObjectSchema.parse(session);
    return ExpressSessionJsonStorageV1Schema.parse(
        normalizeDerivedRuntimeCookieForStorage(rawSessionObject)
    );
}

export function parseRawSessionState(rawSession: unknown): ParsedSessionState {
    if (rawSession === null || rawSession === undefined) {
        return ParsedSessionStateSchema.parse({ kind: 'no_session' });
    }

    const rawSessionObject: Record<string, unknown> = RawSessionObjectSchema.parse(rawSession);
    return parseSessionStateObject(normalizeRuntimeSessionObject(rawSessionObject));
}

function parseStoredSessionState(rawSession: unknown): ParsedSessionState {
    if (rawSession === null || rawSession === undefined) {
        return ParsedSessionStateSchema.parse({ kind: 'no_session' });
    }

    const rawSessionObject: Record<string, unknown> = RawSessionObjectSchema.parse(rawSession);
    return parseSessionStateObject(rawSessionObject);
}

function parseSessionStateObject(rawSessionObject: Record<string, unknown>): ParsedSessionState {
    if (hasPassportUser(rawSessionObject)) {
        const session: AuthenticatedRawSession =
            AuthenticatedRawSessionSchema.parse(rawSessionObject);
        return ParsedSessionStateSchema.parse({
            kind: 'authenticated',
            session,
            databaseSessionData: authenticatedRawSessionToDatabaseSessionData(session),
        });
    }
    if (rawSessionObject.passport !== undefined) {
        AuthenticatedRawSessionSchema.parse(rawSessionObject);
    }

    if (rawSessionObject.guest_token !== undefined && rawSessionObject.guest_token !== null) {
        return ParsedSessionStateSchema.parse({
            kind: 'guest_token',
            session: GuestTokenRawSessionSchema.parse(rawSessionObject),
        });
    }

    if (hasPendingMfaState(rawSessionObject)) {
        return ParsedSessionStateSchema.parse({
            kind: 'pending_mfa',
            session: PendingMfaRawSessionSchema.parse(rawSessionObject),
        });
    }

    return ParsedSessionStateSchema.parse({
        kind: 'unauthenticated',
        session: UnauthenticatedRawSessionSchema.parse(rawSessionObject),
    });
}

function normalizeRuntimeSessionObject(
    rawSessionObject: Record<string, unknown>
): Record<string, unknown> {
    if (rawSessionObject.cookie === undefined || rawSessionObject.cookie === null) {
        return rawSessionObject;
    }

    const runtimeCookie: RuntimeSessionCookie = RuntimeSessionCookieSchema.parse(
        rawSessionObject.cookie
    );
    const normalizedCookie: Record<string, unknown> = { ...runtimeCookie };
    delete normalizedCookie.maxAge;
    return {
        ...rawSessionObject,
        cookie: normalizedCookie,
    };
}

function normalizeDerivedRuntimeCookieForStorage(
    rawSessionObject: Record<string, unknown>
): Record<string, unknown> {
    const rawCookie: unknown = rawSessionObject.cookie;
    if (!hasPrototypeDerivedMaxAge(rawCookie)) {
        return rawSessionObject;
    }
    return normalizeRuntimeSessionObject(rawSessionObject);
}

function hasPrototypeDerivedMaxAge(value: unknown): boolean {
    return value instanceof Object && 'maxAge' in value && !Object.hasOwn(value, 'maxAge');
}

export function parseAuthenticatedRawSession(rawSession: unknown): AuthenticatedRawSession {
    const parsedState: ParsedSessionState = parseRawSessionState(rawSession);
    return ParsedAuthenticatedSessionStateSchema.parse(parsedState).session;
}

export function parseAuthenticatedSessionData(rawSession: unknown): DatabaseSessionData {
    const parsedState: ParsedSessionState = parseRawSessionState(rawSession);
    return ParsedAuthenticatedSessionStateSchema.parse(parsedState).databaseSessionData;
}

export function authenticatedRawSessionToDatabaseSessionData(
    session: AuthenticatedRawSession
): DatabaseSessionData {
    const sessionData: DatabaseSessionData = {
        passport: session.passport,
        ip_address: session.ip_address ?? '',
        user_agent: session.user_agent ?? '',
        cookie: session.cookie,
        active_role_assignment_id: session.active_role_assignment_id,
        impersonator: session.impersonator,
        enable_javascript_console: session.enable_javascript_console ?? true,
    };
    if (session.active_role_assignment_uuid !== undefined) {
        sessionData.active_role_assignment_uuid = session.active_role_assignment_uuid;
    }
    if (session.session_uuid !== undefined) {
        sessionData.session_uuid = session.session_uuid;
    }
    return sessionData;
}

function hasPassportUser(rawSessionObject: Record<string, unknown>): boolean {
    const passportParseResult = RawSessionPassportSchema.safeParse(rawSessionObject.passport);
    return passportParseResult.success;
}

function hasPendingMfaState(rawSessionObject: Record<string, unknown>): boolean {
    return (
        rawSessionObject.pending_mfa_user_id !== undefined ||
        rawSessionObject.pending_totp_user_id !== undefined
    );
}

/**
 * Expire session request
 */
export const ExpireSessionRequestSchema = z.object({
    audit_id: z.number(),
});

export const ExpireSessionUuidRequestSchema: ZodType<
    import('../session.types').ExpireSessionUuidRequest
> = z.object({
    session_uuid: z.string().uuid(),
});

/**
 * Expire user session request (admin)
 */
export const ExpireUserSessionRequestSchema = z.object({
    session_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(),
});

/**
 * Update session console logging flag (admin)
 */
export const UpdateSessionConsoleRequestSchema = z.object({
    session_uuid: z.string().uuid(),
    enable_javascript_console: z.boolean(),
});

/**
 * Session list item
 */
export const SessionListItemSchema: z.ZodType<SessionListItem> = z.object({
    session_uuid: z.string().uuid().optional(),
    audit_id: z.number().optional(),
    user_id: z.number().optional(),
    user_uuid: z.string().uuid().optional(),
    user_email: z.string(),
    ip_address: z.string(),
    user_agent: z.string(),
    expires_at: z.string(), // ISO date string
    active_role_assignment_id: z.number().nullable().optional(),
    active_role_assignment_uuid: z.string().uuid().nullable().optional(),
    active_role_assignment_role: z.enum(Role).nullable().optional(),
    active_role_assignment_organization_uuid: z.string().uuid().nullable().optional(),
    active_role_assignment_organization_name: z.string().nullable().optional(),
    enable_javascript_console: z.boolean(),
    is_current: z.boolean(),
});

/**
 * Get sessions response
 */
export const GetSessionsResponseSchema = z.object({
    sessions: z.array(SessionListItemSchema),
});

export const SessionsSearchRequestSchema: z.ZodType<SessionsSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: z.coerce.number().int().positive().max(500).optional(),
        cursor: z.string().trim().min(1).optional(),
    })
    .strict();

export const ListSessionsPageResponseSchema = z.object({
    sessions: z.array(SessionListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});
